import { PO_STATUSES } from '../purchasing/orders.js';
import { departmentLabel, requisitionStatusOptions } from '../purchasing/requisitions.js';
import type { ReportData, ReportKind, ReportQuery } from './types.js';

/**
 * Raporlar (ADR 0040 #189; `satin-alma-akisi.jpeg` "Reports"): kartlar, rapor görünümü yolu,
 * sütunlar ve dışa aktarılan tablo. Saf modül: ekran, Excel ve PDF aynı tabloyu kullanır.
 */

export const REPORTS_PATH = '/reports';

export const REPORT_KINDS: readonly ReportKind[] = [
  'requisitions',
  'orders',
  'stock',
  'consumption',
];

export type ReportCardKey = ReportKind | 'supplierPerformance' | 'costAnalysis';

/** Tasarımdaki sıra: iki satır üç kart. Hazır olmayanlar "hazırlanıyor" (#189). */
export const REPORT_CARDS: readonly { key: ReportCardKey; ready: boolean }[] = [
  { key: 'requisitions', ready: true },
  { key: 'orders', ready: true },
  { key: 'supplierPerformance', ready: false },
  { key: 'stock', ready: true },
  { key: 'consumption', ready: true },
  { key: 'costAnalysis', ready: false },
];

export type ReportsRoute =
  | { view: 'cards' }
  | { view: 'report'; report: ReportKind; query: ReportQuery };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const STATUS = /^[a-z_]{1,40}$/;

/** Görünüm sorgu dizesinden (`?report=orders&vessel=…&from=…&to=…&status=…`). */
export function reportsRoute(search: string): ReportsRoute {
  const params = new URLSearchParams(search);
  const report = params.get('report');
  if (!report || !(REPORT_KINDS as readonly string[]).includes(report)) return { view: 'cards' };
  const query: ReportQuery = {};
  const vessel = params.get('vessel');
  if (vessel && UUID.test(vessel)) query.vessel = vessel;
  const from = params.get('from');
  if (from && DAY.test(from)) query.from = from;
  const to = params.get('to');
  if (to && DAY.test(to)) query.to = to;
  const status = params.get('status');
  if (status && STATUS.test(status)) query.status = status;
  return { view: 'report', report: report as ReportKind, query };
}

export function reportsPath(route: ReportsRoute): string {
  if (route.view === 'cards') return REPORTS_PATH;
  const params = new URLSearchParams({ report: route.report });
  for (const key of ['vessel', 'from', 'to', 'status'] as const) {
    const value = route.query[key];
    if (value) params.set(key, value);
  }
  return `${REPORTS_PATH}?${params.toString()}`;
}

/**
 * Durum süzgecinin seçenekleri; tüketim raporunda durum yok. Talepte liste ile aynı seçenekler;
 * "In Progress" yalnız ofiste (RFQ'dan türer; M12c-R29).
 */
export function reportStatuses(report: ReportKind, scope: 'ship' | 'office' = 'office'): string[] {
  switch (report) {
    case 'requisitions':
      return requisitionStatusOptions(scope);
    case 'orders':
      return [...PO_STATUSES];
    case 'stock':
      return ['normal', 'low', 'critical'];
    case 'consumption':
      return [];
  }
}

/** Durumun çeviri anahtarı (ekranlardaki rozetlerle aynı metin). */
export function statusKey(report: ReportKind, status: string): string {
  switch (report) {
    case 'requisitions':
      return `purchasing.statuses.${status}`;
    case 'orders':
      return `purchasing.po.statuses.${status}`;
    default:
      return `stock.status.management.${status}`;
  }
}

export type CellKind = 'text' | 'date' | 'number' | 'money' | 'status';
export type Cell = string | number | null;

export interface ReportColumn {
  key: string;
  kind: CellKind;
  label: string;
}

export interface ReportTable {
  report: ReportKind;
  columns: ReportColumn[];
  /** Ham değerler: gün `YYYY-MM-DD`, sayı sayı, durum çevrilmiş metin. */
  rows: Cell[][];
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

type Definition<Row> = readonly (readonly [string, CellKind, (row: Row) => Cell])[];

/**
 * Gemide satın alma fiyatı görünmez (sahip kararı 2026-11-05; ADR 0169 §2): PO raporunun para
 * birimi ve tutar sütunları yok. Talep özetindeki tutar talebin gemide girilen tahminidir; kalır.
 */
const ORDER_PRICE_COLUMNS = new Set(['currency', 'total', 'received', 'remaining']);

function columnsOf(
  data: ReportData,
  t: Translate,
  prices: boolean,
): { kinds: Definition<never>; rows: Cell[][] } {
  const status = (value: string) => t(statusKey(data.report, value));
  switch (data.report) {
    case 'requisitions': {
      const def: Definition<(typeof data.rows)[number]> = [
        ['requisitionNo', 'text', (r) => r.requisitionNo],
        ['vessel', 'text', (r) => r.vesselName],
        ['department', 'text', (r) => departmentLabel(r.department, t)],
        ['requestedBy', 'text', (r) => r.requestedBy],
        ['requestDate', 'date', (r) => r.requestDate],
        ['requiredDate', 'date', (r) => r.requiredDate],
        ['status', 'status', (r) => status(r.status)],
        ['currency', 'text', (r) => r.currency],
        ['total', 'money', (r) => r.total],
        ['poNo', 'text', (r) => r.poNo],
      ];
      return { kinds: def, rows: data.rows.map((row) => def.map(([, , get]) => get(row))) };
    }
    case 'orders': {
      const def: Definition<(typeof data.rows)[number]> = [
        ['poNo', 'text', (r) => r.poNo],
        ['orderDate', 'date', (r) => r.orderDate],
        ['vessel', 'text', (r) => r.vesselName],
        ['supplier', 'text', (r) => r.supplierName],
        ['requisitionNo', 'text', (r) => r.requisitionNo],
        ['status', 'status', (r) => status(r.status)],
        ['expectedDelivery', 'date', (r) => r.expectedDelivery],
        ['currency', 'text', (r) => r.currency],
        ['total', 'money', (r) => r.total],
        ['received', 'money', (r) => r.received],
        ['remaining', 'money', (r) => r.remaining],
      ];
      const shown = prices ? def : def.filter(([key]) => !ORDER_PRICE_COLUMNS.has(key));
      return { kinds: shown, rows: data.rows.map((row) => shown.map(([, , get]) => get(row))) };
    }
    case 'stock': {
      const def: Definition<(typeof data.rows)[number]> = [
        ['vessel', 'text', (r) => r.vesselName],
        ['material', 'text', (r) => r.material],
        ['category', 'text', (r) => r.category],
        ['unit', 'text', (r) => r.unit],
        ['location', 'text', (r) => r.location],
        ['onHand', 'number', (r) => r.onHand],
        ['minQty', 'number', (r) => r.minQty],
        ['maxQty', 'number', (r) => r.maxQty],
        ['status', 'status', (r) => status(r.status)],
      ];
      return { kinds: def, rows: data.rows.map((row) => def.map(([, , get]) => get(row))) };
    }
    case 'consumption': {
      const def: Definition<(typeof data.rows)[number]> = [
        ['vessel', 'text', (r) => r.vesselName],
        ['material', 'text', (r) => r.material],
        ['category', 'text', (r) => r.category],
        ['unit', 'text', (r) => r.unit],
        ['received', 'number', (r) => r.received],
        ['consumed', 'number', (r) => r.consumed],
        ['adjusted', 'number', (r) => r.adjusted],
        ['movements', 'number', (r) => r.movements],
      ];
      return { kinds: def, rows: data.rows.map((row) => def.map(([, , get]) => get(row))) };
    }
  }
}

/** Ekranın, Excel'in ve PDF'in ortak tablosu; gemide `prices: false` (ADR 0169). */
export function reportTable(
  data: ReportData,
  t: Translate,
  options: { prices?: boolean } = {},
): ReportTable {
  const { kinds, rows } = columnsOf(data, t, options.prices !== false);
  return {
    report: data.report,
    columns: kinds.map(([key, kind]) => ({ key, kind, label: t(`reports.columns.${key}`) })),
    rows,
  };
}

const FILE_NAMES: Record<ReportKind, string> = {
  requisitions: 'requisition-summary',
  orders: 'purchase-order-report',
  stock: 'stock-report',
  consumption: 'consumption-report',
};

/** Dosya adı: rapor ve dönem (stok raporu bugünün durumudur; yine de dönemle adlanır). */
export function reportFileName(
  report: ReportKind,
  range: { from: string; to: string },
  extension: 'xlsx' | 'pdf',
): string {
  return `${FILE_NAMES[report]}-${range.from}_${range.to}.${extension}`;
}
